import React from 'react';
import './styles/forms.css'; // Import custom form styles
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { NotificationProvider } from './context/NotificationContext';
import { PermissionProvider } from './context/PermissionContext';
import { FeedbackProvider } from './contexts/FeedbackContext';
import PrivateRoute from './components/PrivateRoute';
import RouterTransition from './components/RouterTransition';
import SecurityHeaders from './security/SecurityHeaders';
import ScrollToTop from './components/ScrollToTop';

// Layouts
import MainLayout from './components/layouts/MainLayout';
import BrandLayout from './components/layouts/BrandLayout';

// Pages
import Home from './pages/Home';
import Login from './pages/Login';
import Register from './pages/Register';
import RegisterBrand from './pages/RegisterBrand';
import ForgotPassword from './pages/ForgotPassword';
import AuthAction from './pages/AuthAction';
import EmailVerification from './pages/EmailVerification';
import VerifyEmail from './pages/VerifyEmail';
import VerifyEmailChange from './pages/VerifyEmailChange';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/influencer/Dashboard';
import Profile from './pages/influencer/Profile';
import Campaigns from './pages/influencer/Campaigns';
import SavedCampaigns from './pages/influencer/SavedCampaigns';
import CampaignDetails from './pages/influencer/CampaignDetails';
import ReviewSubmission from './pages/influencer/ReviewSubmission';
import ProofSubmission from './pages/influencer/ProofSubmission';
import ContentDraftSubmission from './pages/influencer/ContentDraftSubmission';
import Payments from './pages/influencer/Payments';
import Messages from './pages/influencer/Messages';
import Referrals from './pages/influencer/Referrals';
import Notifications from './pages/influencer/Notifications';
import Portfolio from './pages/influencer/Portfolio';
import Reports from './pages/influencer/Reports';
import Applications from './pages/influencer/Applications';
import Settings from './pages/influencer/Settings';

// New Marketing Pages
import ForBrands from './pages/ForBrands';
import ForInfluencers from './pages/ForInfluencers';
import Pricing from './pages/Pricing';
import CampaignsPage from './pages/Campaigns';
import AboutUs from './pages/AboutUs';
import Contact from './pages/Contact';
import Blog from './pages/Blog';
import BlogPost from './pages/BlogPost';
import Press from './pages/Press';
import Careers from './pages/Careers';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import HelpCenter from './pages/HelpCenter';

// Brand Pages
import BrandDashboard from './pages/brand/Dashboard';
import BrandProfile from './pages/brand/Profile';
import BrandCampaigns from './pages/brand/Campaigns';
import BrandApplications from './pages/brand/Applications';
import CreateCampaign from './pages/brand/CreateCampaign';
import CampaignDetail from './pages/brand/CampaignDetail';
import CampaignEdit from './pages/brand/CampaignEdit';
import ContentApproval from './pages/brand/ContentApproval';
import ContentApprovalDetail from './pages/brand/ContentApprovalDetail';
import ContentPreApproval from './pages/brand/ContentPreApproval';
import ContentPreApprovalDetail from './pages/brand/ContentPreApprovalDetail';
import BrandInfluencers from './pages/brand/Influencers';
import InfluencerProfile from './pages/brand/InfluencerProfile';
import BrandAnalytics from './pages/brand/Analytics';
import BrandPayments from './pages/brand/Payments';
import Billing from './pages/brand/Billing';
import BrandReports from './pages/brand/Reports';
import BrandSettings from './pages/brand/Settings';

// Admin Pages
import AdminLayout from './components/layouts/AdminLayout';
import AdminDashboard from './pages/admin/Dashboard';
import AdminUsers from './pages/admin/Users';
import AdminCampaigns from './pages/admin/Campaigns';
import AdminReports from './pages/admin/Reports';

// Tracking Pages
import TrackingRedirect from './pages/TrackingRedirect';

// Test Components
import EmailTest from './components/EmailTest';

import './styles/tailwind.css';

function App() {
  return (
    <Router>
      <SecurityHeaders />
      <ScrollToTop />
      <AuthProvider>
        <NotificationProvider>
          <PermissionProvider>
            <FeedbackProvider>
              <RouterTransition>
            <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/register-brand" element={<RegisterBrand />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/auth-action" element={<AuthAction />} />
          <Route path="/email-verification" element={<EmailVerification />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/verify-email-change" element={<VerifyEmailChange />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/test-email" element={<EmailTest />} />
          
          {/* Marketing Pages */}
          <Route path="/campaigns" element={<CampaignsPage />} />
          <Route path="/for-brands" element={<ForBrands />} />
          <Route path="/for-influencers" element={<ForInfluencers />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/about-us" element={<AboutUs />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/press" element={<Press />} />
          <Route path="/careers" element={<Careers />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
          <Route path="/help-center" element={<HelpCenter />} />
          
          {/* Tracking Routes */}
          <Route path="/t/:shortCode" element={<TrackingRedirect />} />
          
          {/* Influencer Routes */}
          <Route path="/influencer" element={
            <PrivateRoute>
              <MainLayout />
            </PrivateRoute>
          }>
            <Route index element={<Dashboard />} />
            <Route path="profile" element={<Profile />} />
            <Route path="campaigns" element={<Campaigns />} />
            <Route path="saved" element={<SavedCampaigns />} />
            <Route path="campaigns/:id" element={<CampaignDetails />} />
            <Route path="applications" element={<Applications />} />
            <Route path="reviews/submit/:campaignId" element={<ReviewSubmission />} />
            <Route path="proof/submit/:campaignId" element={<ProofSubmission />} />
            <Route path="draft/submit/:campaignId" element={<ContentDraftSubmission />} />
            <Route path="payments" element={<Payments />} />
            <Route path="messages" element={<Messages />} />
            <Route path="referrals" element={<Referrals />} />
            <Route path="notifications" element={<Notifications />} />
            <Route path="portfolio" element={<Portfolio />} />
            <Route path="reports" element={<Reports />} />
            <Route path="settings" element={<Settings />} />
          </Route>
          
          {/* Brand Routes */}
          <Route path="/brand" element={
            <PrivateRoute>
              <BrandLayout />
            </PrivateRoute>
          }>
            <Route index element={<BrandDashboard />} />
            <Route path="profile" element={<BrandProfile />} />
            <Route path="campaigns" element={<BrandCampaigns />} />
            <Route path="applications" element={<BrandApplications />} />
            <Route path="campaigns/create" element={<CreateCampaign />} />
            <Route path="campaigns/:id" element={<CampaignDetail />} />
            <Route path="campaigns/:id/edit" element={<CampaignEdit />} />
            <Route path="content-approval" element={<ContentApproval />} />
            <Route path="content-approval/:id" element={<ContentApprovalDetail />} />
            <Route path="content-pre-approval" element={<ContentPreApproval />} />
            <Route path="content-pre-approval/:id" element={<ContentPreApprovalDetail />} />
            <Route path="influencers" element={<BrandInfluencers />} />
            <Route path="influencers/:id" element={<InfluencerProfile />} />
            <Route path="analytics" element={<BrandAnalytics />} />
            <Route path="payments" element={<BrandPayments />} />
            <Route path="billing" element={<Billing />} />
            <Route path="reports" element={<BrandReports />} />
            <Route path="messages" element={<Messages />} />
            <Route path="notifications" element={<Notifications />} />
            <Route path="settings" element={<BrandSettings />} />
          </Route>
          
          {/* Admin Routes */}
          <Route path="/admin" element={
            <PrivateRoute>
              <AdminLayout />
            </PrivateRoute>
          }>
            <Route index element={<AdminDashboard />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="campaigns" element={<AdminCampaigns />} />
            <Route path="reports" element={<AdminReports />} />
          </Route>
            </Routes>
              </RouterTransition>
            </FeedbackProvider>
          </PermissionProvider>
        </NotificationProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
